import { Stage } from "stage/stage/stage";
import { ScreenWorldSpaceUtils } from "stage/utils/screenWorldSpace";
import {
  visibleHeightAtZDepth,
  visibleWidthAtZDepth,
} from "stage/utils/utils.stage";
import * as THREE from "three";

import * as config from "./home.config";
import assetPath from "utils/assetPath.utils";

class HomeStage extends Stage {
  private objects: any[] = [];
  private lastTime: number = 0;
  private heroRoot: THREE.Group;
  constructor(canvas: HTMLCanvasElement, private onLoaded: () => void) {
    super(canvas);

    // set bg color
    this.scene.background = new THREE.Color(0xf1efea);

    // create a custom root for hero
    this.heroRoot = new THREE.Group();
    this.scene.add(this.heroRoot);

    // load all assets
    this.load(
      {
        models: [
          assetPath("/assets/models/Brie.gltf"),
          assetPath("/assets/models/Chair.gltf"),
          assetPath("/assets/models/Cup.gltf"),
          assetPath("/assets/models/Eiffel_Tower.gltf"),
          assetPath("/assets/models/Macaron.gltf"),
          assetPath("/assets/models/Spliff.gltf"),
          assetPath("/assets/models/Table.gltf"),
          assetPath("/assets/models/NotInParis.gltf"),
        ],
        textures: [],
        hdri: [],
        fonts: [],
      },
      this.init
    );
  }
  private init = () => {
    console.log("init");
    this.onLoaded();
    // set camera
    this.camera.position.set(0, 0, 10);
    // add objects and light
    this.addObjects();
    this.addLights();
    // this.addText();
    this.addTextMesh();
    // set custom animation loop
    this.renderer.setAnimationLoop(this.renderSubscene);
  };

  private addObjects = () => {
    // define a material
    const material = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      metalness: 0,
      roughness: 0,
      flatShading: true,
    });

    // generate objects for each stage config item
    let placementRatioX = visibleWidthAtZDepth(0, this.camera) * 0.6;
    let scallarRatio =
      (15 /
        Math.sqrt(
          Math.pow(visibleWidthAtZDepth(0, this.camera), 2) +
            Math.pow(visibleHeightAtZDepth(0, this.camera) * 2, 2)
        )) *
      0.8;

    if (this.canvas.width < this.canvas.height) {
      scallarRatio *= 0.8;
      placementRatioX *= 1.2;
    }

    config.stageConfig.items.map((item, key) => {
      const object = this.assets.models[item.assetIndex].scene
        .children[0] as THREE.Mesh;

      // scale
      object.scale.multiplyScalar(item.scallar * scallarRatio);
      object.geometry.center();
      // position
      const _x =
        -placementRatioX / 2 +
        (placementRatioX / (config.stageConfig.items.length - 1)) * key;
      object.position.set(_x, 0, 0);

      // material
      // @ts-ignore
      object.material.metalness = 0;
      // object.material = material;

      // add the objects
      this.heroRoot.add(object);
      this.objects.push(object);

      return object;
    });

    this.renderer.render(this.scene, this.camera);
  };

  private addLights = () => {
    const width = visibleWidthAtZDepth(0, this.camera);
    const height = visibleHeightAtZDepth(0, this.camera);

    const ambientLight = new THREE.AmbientLight(0xffffff, 1.25);
    this.scene.add(ambientLight);

    const giuCamera = this.gui.addFolder("Ambient");
    giuCamera.add(ambientLight, "intensity", 0, 2, 0.05);

    const directionalLight = new THREE.DirectionalLight("#fff", 2);
    directionalLight.position.set(width, height / 2, this.camera.position.z);
    // directionalLight.target = this.heroRoot;
    this.scene.add(directionalLight);

    // const helper = new THREE.DirectionalLightHelper(directionalLight, 5);
    // this.scene.add(helper);

    const guiDL = this.gui.addFolder("Directional Light");
    guiDL.add(directionalLight, "intensity", 0, 2, 0.05);
    guiDL.add(directionalLight.position, "x", -width, width, 0.05);
    guiDL.add(directionalLight.position, "y", -height, height, 0.05);
    guiDL.add(directionalLight.position, "z", 0, this.camera.position.z, 0.05);
  };

  private addTextMesh = () => {
    let scallarRatio =
      2200 /
      Math.sqrt(
        Math.pow(visibleWidthAtZDepth(0, this.camera), 2) +
          Math.pow(visibleHeightAtZDepth(0, this.camera) * 2, 2)
      );

    if (this.canvas.width < this.canvas.height) {
      scallarRatio *= 0.5;
    }

    const object = this.assets.models[7].scene.children[0] as THREE.Mesh;

    const bbox = new THREE.Box3().setFromObject(object);
    let width = visibleWidthAtZDepth(0, this.camera) * 0.8;
    this.canvas.width > this.canvas.height && (width *= 0.8);

    object.position.set(0, 0, 0);
    object.rotation.set(Math.PI / 2, 0, 0);
    object.geometry.center();

    object.scale.multiplyScalar(1 / bbox.max.x * width);

    // object.geometry.center();
    const material = new THREE.MeshBasicMaterial({
      color: new THREE.Color(0x2a7050),
    });
    object.material = material;

    this.heroRoot.add(object);

    console.log(this.assets.models[7].scene);

    // this.scene.add(this.assets.models[7].scene)
  };

  // **************
  //     RENDER
  // **************
  private renderSubscene = (elapsedTime: any, deltaTime: any) => {
    const tDelta = elapsedTime - this.lastTime;
    this.lastTime = elapsedTime;

    this.objects.forEach((item, key) => {
      const itemConfig = config.stageConfig.items[key];
      const speed = itemConfig.speed;
      item.rotation.y += (0.01 / 16) * tDelta * speed * 0.6;
      item.rotation.z += (0.01 / 16) * tDelta * speed * 0.6;

      const screenHeight = visibleHeightAtZDepth(0, this.camera);
      const radius = screenHeight * 0.5 * itemConfig.yOffset;
      const timeFactor = 1000;
      const offsetRad = itemConfig.offsetRad;
      // const angle = Math.sin(this.lastTime*0.001)*Math.PI;
      const newPos = new THREE.Vector3(
        0,
        Math.cos(elapsedTime / timeFactor + offsetRad) * radius * 0.6,
        Math.sin(elapsedTime / timeFactor + offsetRad) * radius * 0.6,
      );

      item.position.y = newPos.y;
      item.position.z = newPos.z;
    });

    this.heroRoot.position.y =
      (window.scrollY / window.innerHeight) *
      visibleWidthAtZDepth(0, this.camera);

    // this.camera.position.z += 0.01;
    this.render(elapsedTime, deltaTime);
  };
}

export default HomeStage;
